// CRM 2.0 Application for Booking stage (M3-F2 + M3-F3..F7; rows M3.16, M3.24, M3.44, DC-74 / DC-146 /
// DC-167): one next-step box, then the step list (5–7 rows): booking amount · RM (while waiting) ·
// Application for Booking · Agreement KYC · review draft · parking (when it applies) · stamp duty &
// charges · Final Review. The 15-day ring shows only once a CRM is tagged (DC-83).
// card.afb / card.kyc / card.reviewDraft come from their M3 functions; missing = not started yet.
// M4-F6 (DC-105 / DC-108 / DC-110): card.discounts - an open loyalty / referral decision shows the red
// "Action needed" box and locks the later rows; a removal by VJ shows the red note (only when shown to the customer).
import React from "react";
import WaitingForRm from "../WaitingForRm";
import { NextBox, Steps, DaysRing, Link, Btn, niceDate, inr, maskMobile } from "../Crm2Parts";
import css from "../Crm2Booking.module.css";

const SDRN = "Stamp Duty, Registration and Legal Charges";
const stepOf = (card, code) => (card.steps?.[1] || []).find((s) => s.stepCode === code) || null;
const isDone = (s) => s?.status === "DONE";

export default function AfbStage({ card, actions = {} }) {
	if (card.bookingState === "WAITING_RM") return <WaitingForRm info={card} />;

	const afb = card.afb || {};
	const kyc = card.kyc || {};
	const rd = card.reviewDraft || {};
	const afbOk = !!afb.confirmedAt || isDone(stepOf(card, "AFB_CONFIRMED"));
	const kycOk = !!kyc.submittedAt || card.kycFilled;
	const correction = kyc.correction?.reason || null;
	const rdReady = rd.status === "READY";
	const rdConfirmed = !!rd.allConfirmed;
	const park = stepOf(card, "PARKING");
	const parkApplies = !!park && park.status !== "NA";
	const parkOk = isDone(park);
	const sdrOk = isDone(stepOf(card, "SDR_PAYMENT"));
	const frOk = isDone(stepOf(card, "FINAL_REVIEW"));
	const ring = card.deadline ? <DaysRing left={card.deadline.daysLeft} total={15} /> : null;
	const due = card.deadline ? niceDate(card.deadline.at) : null;
	const disc = card.discounts || null;
	const dp = !frOk && !!disc?.decisionOpen;
	const loy = disc?.loyalty || {};
	const exN = loy.existingCustomer || "the existing VJ customer";
	const exF = String(exN).split(" ")[0];
	const ptsTxt = loy.points ? `${Number(loy.points).toLocaleString("en-IN")} VJOP points` : "VJOP referral points";
	const isRef = loy.fromType === "Referral";
	const od = (m) => actions.openDiscount && (() => actions.openDiscount(m));
	const rmv = !frOk && disc?.removedByVj;
	const hr = rmv ? (
		<div className={css.badBox} style={{ marginBottom: "1rem" }}>
			<b>VJ has removed your loyalty discount.</b> Your booking is now{" "}
			{rmv.moveTo === "DIRECT" ? (
				<>
					a <b>Direct</b> booking: no loyalty discount and no VJOP referral points
				</>
			) : (
				<>
					a <b>Referral</b> booking: {exN} earns {rmv.points ? `${Number(rmv.points).toLocaleString("en-IN")} VJOP points` : "VJOP referral points"} in VJOP after the agreement is registered
				</>
			)}
			. {rmv.reason ? `Reason: ${rmv.reason}. ` : ""}Your total unit cost is now <b>{inr(rmv.after || card.money?.unitCostTotal)}</b>
			{rmv.before ? ` (was ${inr(rmv.before)})` : ""}. Please confirm the new review draft by OTP.{card.crm ? ` Questions: your CRM ${card.crm.name}${card.crm.phone ? `, ${card.crm.phone}` : ""}.` : ""}
		</div>
	) : null;

	// next-step box: the first thing waiting for the customer (prototype stAFB order)
	let nx;
	if (frOk) nx = <NextBox who="done" title="Application for Booking completed">VJ approved your booking and sent the allotment letter. Next stage: Allotment Letter.</NextBox>;
	else if (dp) nx = (
		<NextBox
			who="bad"
			title={disc.case === "OFFER" ? "Decide on your loyalty discount" : "Choose: add the existing customer back or change to Referral"}
			side={ring}
			actions={
				disc.case === "OFFER" ? (
					<>
						{od("disc") && <Btn onClick={od("disc")}>View details</Btn>}
						{od("final:loy") && <Btn primary onClick={od("final:loy")}>Avail discount</Btn>}
						{od("final:keep") && <Btn onClick={od("final:keep")}>{isRef ? "Keep referral" : "No, thanks"}</Btn>}
					</>
				) : (
					<>
						{od("pick:addback") && <Btn onClick={od("pick:addback")}>{`Add ${exF} back`}</Btn>}
						{od("pick:toref") && <Btn primary onClick={od("pick:toref")}>Change to Referral</Btn>}
					</>
				)
			}
		>
			{disc.case === "OFFER"
				? `You can get ${inr(loy.amount)} off your total unit cost${isRef ? ` (your booking becomes a Loyalty booking; the referrer does not earn ${ptsTxt})` : ""}, or keep ${isRef ? `the referral (${exF} earns ${ptsTxt} in VJOP after the agreement is registered)` : "the actual unit price"}.`
				: `${exN}, the existing VJ customer, is not an applicant, so the loyalty discount cannot apply. Add them back, or change your booking to Referral (${exF} gets VJOP referral points).`}
			<div className={css.lock} style={{ marginTop: "1rem" }}>
				🔒 Your next steps ({rdReady ? "review draft OTP, " : "review draft, "}parking, payment and Final Review) are locked until you decide, because your choice changes your total unit cost and your agreement.
			</div>
			<div className={css.muted} style={{ marginTop: "0.8rem" }}>
				We remind you every day by email and WhatsApp until you decide. Your 15 days keep running{due ? `: finish by ${due}` : ""}. Nothing is decided for you; if the days run out, the booking is cancelled under the normal rule unless your CRM gives you more days.
			</div>
		</NextBox>
	);
	else if (afb.reconfirm) nx = (
		<NextBox who="bad" title="Confirm your Application for Booking again" side={ring} actions={actions.openAfb && <Btn primary onClick={actions.openAfb}>View &amp; Confirm</Btn>}>
			Your unit was changed. We made a new Application for Booking with the new unit, price and cost sheet and sent it again by email and WhatsApp. Read it and confirm with the OTP sent to {maskMobile(afb.mobile)}. Your 15 days do not restart.
		</NextBox>
	);
	else if (!afbOk) nx = (
		<NextBox who="bad" title="Confirm your Application for Booking" side={ring} actions={actions.openAfb && <Btn primary onClick={actions.openAfb}>View &amp; Confirm</Btn>}>
			Your Application for Booking is ready: your unit, price, payment plan and cost sheet. Read it and confirm with the OTP sent to {maskMobile(afb.mobile)}, the contact person of this booking. Your Agreement KYC opens after you confirm.
		</NextBox>
	);
	else if (correction) nx = (
		<NextBox who="bad" title="Correct your Agreement KYC" side={ring} actions={actions.openKyc && <Btn primary onClick={() => actions.openKyc("correct")}>Correct Agreement KYC</Btn>}>
			At Final Review your CRM asked: "{correction}" After you save, you confirm a new review draft by OTP. Your plan, parking and the stamp duty &amp; charges you paid stay as they are.
		</NextBox>
	);
	else if (!kycOk) nx = (
		<NextBox who="you" title="Fill your Agreement KYC" side={ring} actions={actions.openKyc && <Btn primary onClick={() => actions.openKyc("new")}>Agreement KYC</Btn>}>
			Add details of every applicant, upload Aadhaar and PAN, and add your bank account. Takes about 10 minutes.
		</NextBox>
	);
	else if (!rdReady && !rdConfirmed) nx = rd.status === "FAILED"
		? <NextBox who="vj" title="Review draft not generated yet" side={ring}>We are trying again. You will get it by email and WhatsApp.</NextBox>
		: <NextBox who="vj" title="We are making your review draft" side={ring}>From your Agreement KYC, usually within a few minutes. Your CRM checks your KYC once, at Final Review.</NextBox>;
	else if (!rdConfirmed) nx = (
		<NextBox who="you" title={rd.revisionNo > 1 ? `Confirm your review draft again (Revision ${rd.revisionNo})` : "Read your review draft and confirm"} side={ring} actions={actions.openReview && <Btn primary onClick={actions.openReview}>Review Draft &amp; Confirm</Btn>}>
			{rd.revisionNo > 1 ? `Your details changed, so we made a new review draft. Revision ${rd.revisionNo - 1} is no longer valid. ` : "The draft agreement is made from your KYC. Check the names and details, then "}
			{rd.revisionNo > 1 ? "Every applicant confirms it with the OTP sent to their own mobile." : "every applicant confirms with the OTP sent to their own mobile."}
		</NextBox>
	);
	else if (parkApplies && !parkOk) nx = (
		<NextBox who="you" title="Connect with your dedicated Relationship Manager for parking" side={ring}>
			Tell your Relationship Manager which parking you prefer (included or upgraded). They select it for you. After that you pay {SDRN}.
			{card.crm && <div><b>{card.crm.name}</b>{card.crm.phone ? ` · ${card.crm.phone}` : ""}</div>}
		</NextBox>
	);
	else if (!sdrOk) nx = (
		<NextBox who="you" title={`Pay ${SDRN}`} side={ring} actions={actions.openVa && <Btn primary onClick={actions.openVa}>View Virtual Account Details</Btn>}>
			Transfer to your virtual account by NEFT / RTGS / IMPS{due ? ` before ${due}` : ""}. VJ pays the stamp duty, registration and legal charges for your agreement from it.
		</NextBox>
	);
	else nx = <NextBox who="vj" title="Your CRM is doing the Final Review" side={ring}>All your steps are done. Your CRM now checks your Agreement KYC and documents, review draft, discounts, parking and payment. You will get the allotment letter by email and WhatsApp.</NextBox>;

	const afbRow = afbOk
		? ["ok", "Application for Booking", `Confirmed${afb.confirmedAt ? ` on ${niceDate(afb.confirmedAt)}` : ""}${afb.versionNo > 1 ? ` · version ${afb.versionNo} is the latest` : ""}`, actions.openAfb && <Link onClick={actions.openAfb}>View</Link>]
		: ["bad", "Application for Booking", "Action needed · confirm by OTP", actions.openAfb && <Link onClick={actions.openAfb}>View &amp; Confirm</Link>];
	const kycRow = !afbOk
		? ["todo", "Agreement KYC", "🔒 Opens after you confirm your Application for Booking", null]
		: correction
			? ["bad", "Agreement KYC", "To correct · see the reason above", actions.openKyc && <Link onClick={() => actions.openKyc("correct")}>Correct</Link>]
			: kycOk
				? ["ok", "Agreement KYC", `Submitted${kyc.submittedAt ? ` ${niceDate(kyc.submittedAt)}` : ""}${kyc.applicants ? ` · ${kyc.applicants} applicant${kyc.applicants > 1 ? "s" : ""}` : ""}${frOk ? " · checked at Final Review" : " · your CRM checks it at Final Review"}`, !frOk && (
						<span style={{ display: "inline-flex", gap: "1.2rem", flexWrap: "wrap" }}>
							{actions.openKyc && <Link onClick={() => actions.openKyc("edit")}>Edit (applicants too)</Link>}
							{od(disc?.case === "OFFER" ? "gift" : "disc") && <Link onClick={od(disc?.case === "OFFER" ? "gift" : "disc")}>{disc?.case === "OFFER" ? "Decide on loyalty discount" : "Discount checks"}</Link>}
						</span>
					)]
				: ["now", "Agreement KYC", "Not filled", actions.openKyc && <Link onClick={() => actions.openKyc("new")}>Open</Link>];
	const rdRow = !kycOk
		? ["todo", "Review draft", "After you submit your KYC", null]
		: rdConfirmed
			? ["ok", "Review draft", `Revision ${rd.revisionNo || 1} confirmed by OTP${rd.confirmedBy ? ` · ${rd.confirmedBy}` : ""}`, actions.openReview && <Link onClick={actions.openReview}>View</Link>]
			: rdReady
				? ["now", "Review draft", `Revision ${rd.revisionNo || 1} ready · confirm by OTP${rd.pendingNames?.length ? ` · waiting for ${rd.pendingNames.join(", ")}` : ""}`, actions.openReview && <Link onClick={actions.openReview}>Open</Link>]
				: ["wait", "Review draft", rd.status === "FAILED" ? "Not generated yet · we are trying again" : "Being made from your KYC", null];
	// M5-F7 (DC-123 / DC-88): skipped by the CRM → "No parking for this unit"; selected → the parking and its kind
	const pk = card.parking?.slots?.[0];
	const parkText = card.parking?.skipped ? "No parking for this unit (by your CRM)" : pk ? `${pk.upgraded ? "Upgraded" : "Included"} parking ${pk.number}${pk.type ? ` · ${pk.type.toLowerCase()}` : ""} · selected by your CRM` : "Selected by your CRM";
	const parkRow = parkOk ? ["ok", "Parking (by your CRM)", parkText, null] : rdConfirmed ? ["wait", "Parking (by your CRM)", card.crm ? `Connect with your Relationship Manager ${card.crm.name}${card.crm.phone ? ` · ${card.crm.phone}` : ""}` : "Connect with your Relationship Manager", null] : ["todo", "Parking (by your CRM)", "After your review draft is confirmed", null];
	// M6-F11 (DC-123): paid earlier, the price went up → pay the difference into the virtual account
	const sdrDiff = card.money?.sdr?.difference || 0;
	const sdrRow = sdrDiff > 0 ? ["bad", SDRN, `Pay the difference ${inr(sdrDiff)} into your virtual account · Final Review waits for it`, actions.openVa && <Link onClick={actions.openVa}>Account details</Link>] : sdrOk ? ["ok", SDRN, "Paid into your virtual account", null] : parkApplies && !parkOk ? ["todo", SDRN, "🔒 Opens after your CRM selects parking · amount is final then", null] : [rdConfirmed ? "now" : "todo", SDRN, "Into your virtual account", actions.openVa && <Link onClick={actions.openVa}>Account details</Link>];
	const frRow = frOk ? ["ok", "Final Review by your CRM", "Approved", null] : sdrOk && rdConfirmed ? ["wait", "Final Review by your CRM", "With your CRM: KYC, documents, discounts, parking, payment", null] : ["todo", "Final Review by your CRM", "After all steps are done", null];

	const rows = [["ok", "Booking amount", `Paid${card.dates?.appliedOn ? ` ${niceDate(card.dates.appliedOn)}` : ""} · ${inr(card.money?.bookingTotal)}`, null], afbRow, kycRow, rdRow]
		.concat(parkApplies ? [parkRow] : [])
		.concat([sdrRow, frRow]);
	// DC-105: while the decision is open the KYC row asks to decide and every later step waits
	if (dp)
		rows.forEach((r, i) => {
			if (r[1] === "Agreement KYC") {
				r[0] = "bad";
				r[2] = "Submitted · discount decision pending";
				r[3] = od("disc") && <Link onClick={od("disc")}>Decide now</Link>;
			} else if (i > 2 && r[0] !== "ok") {
				r[0] = "todo";
				r[2] = "🔒 Locked until you decide on the discount";
				r[3] = null;
			}
		});
	const doneN = rows.filter((r) => r[0] === "ok").length;
	return (
		<>
			{hr}
			{nx}
			<Steps title="Application for Booking" sub={frOk ? "completed" : `${doneN} of ${rows.length} done${due ? ` · finish by ${due}` : ""}`} rows={rows} />
		</>
	);
}
